import React, { useState, useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import {
  showToast,
  showAlertWithHtml,
  showPartPaymentGoCardlessNotice,
  partPaymentGoCardlessWarningOf
} from "../../utils/utils";
import RepayDatePickerV1 from "../../components/datepicker/RepayDatePickerV1";
import PropTypes from "prop-types";
import {
  MAX_DAY_COUNT,
  showToastType
} from "../../utils/constants/appConstants";
import { useDispatch } from "react-redux";
import { postDateThunk, putDateThunk } from "../../redux/datePickerSlice";
import { alertActions, dashboard } from "../../locales/en";

const DatePicker = (props) => {
  const {
    invData,
    getDiffOriginalDueDateAndRepayDate,
    isFromPayment,
    disabled
  } = props;
  const { id } = useParams();
  const [currentDate, setCurrentDate] = useState("");

  const dispatch = useDispatch();

  // The save checks for a part payment again, because one can be recorded after the
  // preview. If staff have not acknowledged it, the save changes nothing and returns the
  // GoCardless warning: Confirm sends it again as acknowledged, Cancel resolves to null.
  // The thunk rejects with the response itself.
  const putRepayDate = async (payload) => {
    const put = (body) => dispatch(putDateThunk(body)).unwrap();
    try {
      return await put(payload);
    } catch (rejected) {
      const warning = partPaymentGoCardlessWarningOf(rejected);
      if (!warning) {
        throw rejected;
      }
      const choice = await showPartPaymentGoCardlessNotice(warning);
      if (!choice.isConfirmed) {
        return null;
      }
      return put({ ...payload, partPaymentAcknowledged: true });
    }
  };

  const handleGroupRepayDate = async (date) => {
    const formattedDate = date ? date.toLocaleDateString("en-CA") : "";
    setCurrentDate(formattedDate);

    try {
      const dataToSendPost = {
        quoteIds: [id],
        repaymentDate: formattedDate
      };

      const postresponse = await dispatch(
        postDateThunk(dataToSendPost)
      ).unwrap();

      const messageHtml = postresponse.data.data;

      const partPaymentWarning = messageHtml?.partPaymentGoCardlessWarning;
      if (partPaymentWarning) {
        const acknowledged =
          await showPartPaymentGoCardlessNotice(partPaymentWarning);
        if (!acknowledged.isConfirmed) {
          setCurrentDate(invData?.repaymentDate);
          return;
        }
      }

      if (!messageHtml?.quoteDetails || messageHtml.quoteDetails.length === 0) {
        if (!isFromPayment) {
          showToast(messageHtml?.message?.[0], showToastType.error);
          return;
        }
      }

      const modalMessage =
        messageHtml?.quoteDetails && messageHtml.quoteDetails.length > 0
          ? ` <p><strong>FT${messageHtml?.quoteDetails?.[0]?.quoteId}</strong> - ${messageHtml?.message?.[0]}</p>
                ${messageHtml?.quoteDetails?.[0]?.dayCount > MAX_DAY_COUNT ? `<p class="info"><span>🛈</span>${messageHtml?.message?.[1]}</p>` : ""}`
          : ` <p><strong>FT${id}</strong> - ${messageHtml?.message?.[0]}</p>`;

      const confirmText =
        isFromPayment &&
        (!messageHtml?.quoteDetails || messageHtml.quoteDetails.length === 0)
          ? alertActions.ChangeItAnyway
          : alertActions.Confirm;

      const result = await showAlertWithHtml(
        dashboard.repayDateAlert.title,
        modalMessage,
        "",
        confirmText,
        alertActions.Cancel,
        "alert-xl"
      );

      const dataToSend = {
        quoteIds: [id],
        repaymentDate: formattedDate
      };

      if (result.isConfirmed) {
        const response = await putRepayDate({
          ...dataToSend,
          partPaymentAcknowledged: Boolean(partPaymentWarning)
        });
        if (!response) {
          setCurrentDate(invData?.repaymentDate);
          return;
        }
        if (response.status === 200) {
          showToast(response.data.message, showToastType.success);
          getDiffOriginalDueDateAndRepayDate(
            response?.data?.data.diffOriginalDueDateAndRepayDate
          );
        }
      }
    } catch (error) {
      showToast(
        error?.data?.message || showToastType.errorText,
        showToastType.error
      );
    }
  };

  useEffect(() => {
    setCurrentDate(invData?.repaymentDate);
  }, [invData]);

  return (
    <RepayDatePickerV1
      onChange={handleGroupRepayDate}
      value={currentDate}
      disabled={disabled}
    />
  );
};
DatePicker.propTypes = {
  invData: PropTypes.object,
  getDiffOriginalDueDateAndRepayDate: PropTypes.func,
  isFromPayment: PropTypes.bool,
  disabled: PropTypes.bool
};

export default DatePicker;
